import React from "react";
import { Divider, Stack } from "@mui/material";
import {
  ColorSetting,
  EditorGroup,
  NumberSetting,
  SegmentedChoiceSetting,
} from "./PageEditorControls";
import { resolveProductsPageVisual } from "../productsPageVisualOwnership";

function color(label, value, path, setVisual) {
  return <ColorSetting label={label} value={value} onChange={(next) => setVisual(path, next)} />;
}

function SurfaceFields({ label, value, path, setVisual }) {
  return (
    <EditorGroup title={label}>
      {color("Fondo", value.backgroundColor, `${path}.backgroundColor`, setVisual)}
      {color("Borde", value.borderColor, `${path}.borderColor`, setVisual)}
      <NumberSetting label="Grosor del borde" value={value.borderWidth} min={0} max={16} onChange={(next) => setVisual(`${path}.borderWidth`, next)} />
      <NumberSetting label="Radio" value={value.borderRadius} min={0} max={80} onChange={(next) => setVisual(`${path}.borderRadius`, next)} />
      {color("Color de sombra", value.shadowColor, `${path}.shadowColor`, setVisual)}
      <NumberSetting label="Opacidad de sombra" value={value.shadowOpacity} min={0} max={1} step={0.05} onChange={(next) => setVisual(`${path}.shadowOpacity`, next)} />
      <NumberSetting label="Sombra X" value={value.shadowX} min={-120} max={120} onChange={(next) => setVisual(`${path}.shadowX`, next)} />
      <NumberSetting label="Sombra Y" value={value.shadowY} min={-120} max={120} onChange={(next) => setVisual(`${path}.shadowY`, next)} />
      <NumberSetting label="Blur de sombra" value={value.shadowBlur} min={0} max={220} onChange={(next) => setVisual(`${path}.shadowBlur`, next)} />
      <NumberSetting label="Expansión de sombra" value={value.shadowSpread} min={-100} max={100} onChange={(next) => setVisual(`${path}.shadowSpread`, next)} />
    </EditorGroup>
  );
}

function ButtonFields({ label, value, path, setVisual }) {
  return (
    <EditorGroup title={label}>
      {color("Texto", value.textColor, `${path}.textColor`, setVisual)}
      {color("Fondo", value.backgroundColor, `${path}.backgroundColor`, setVisual)}
      {color("Borde", value.borderColor, `${path}.borderColor`, setVisual)}
      {color("Texto hover", value.hoverTextColor, `${path}.hoverTextColor`, setVisual)}
      {color("Fondo hover", value.hoverBackgroundColor, `${path}.hoverBackgroundColor`, setVisual)}
      {color("Borde hover", value.hoverBorderColor, `${path}.hoverBorderColor`, setVisual)}
      {color("Focus", value.focusRingColor, `${path}.focusRingColor`, setVisual)}
      {color("Texto deshabilitado", value.disabledTextColor, `${path}.disabledTextColor`, setVisual)}
      {color("Fondo deshabilitado", value.disabledBackgroundColor, `${path}.disabledBackgroundColor`, setVisual)}
      {color("Borde deshabilitado", value.disabledBorderColor, `${path}.disabledBorderColor`, setVisual)}
      <NumberSetting label="Grosor del borde" value={value.borderWidth} min={0} max={16} onChange={(next) => setVisual(`${path}.borderWidth`, next)} />
      <NumberSetting label="Radio" value={value.borderRadius} min={0} max={80} onChange={(next) => setVisual(`${path}.borderRadius`, next)} />
    </EditorGroup>
  );
}

export function ProductsPageGeneralVisualEditor({ visual, setVisual }) {
  const v = resolveProductsPageVisual(visual);
  return (
    <Stack spacing={1.5}>
      <EditorGroup title="Página" description="Este fondo pertenece sólo a /productos.">
        {color("Fondo general", v.page.backgroundColor, "page.backgroundColor", setVisual)}
      </EditorGroup>
      <EditorGroup title="Header · fondo" description="El gradiente guarda sus colores reales; no depende del theme.">
        <SegmentedChoiceSetting
          label="Tipo"
          value={v.header.background.mode}
          onChange={(next) => setVisual("header.background.mode", next)}
          options={[{ value: "gradient", label: "Gradiente" }, { value: "solid", label: "Sólido" }]}
        />
        {v.header.background.mode === "solid"
          ? color("Color", v.header.background.solidColor, "header.background.solidColor", setVisual)
          : <>
              {color("Acento radial", v.header.background.accentColor, "header.background.accentColor", setVisual)}
              {color("Inicio", v.header.background.secondaryColor, "header.background.secondaryColor", setVisual)}
              {color("Final", v.header.background.baseColor, "header.background.baseColor", setVisual)}
            </>}
      </EditorGroup>
      <EditorGroup title="Estados de carga y error">
        {color("Loading · fondo", v.states.loading.backgroundColor, "states.loading.backgroundColor", setVisual)}
        {color("Loading · texto", v.states.loading.textColor, "states.loading.textColor", setVisual)}
        {color("Loading · progreso", v.states.loading.progressColor, "states.loading.progressColor", setVisual)}
        <Divider />
        {color("Error · fondo", v.states.error.backgroundColor, "states.error.backgroundColor", setVisual)}
        {color("Error · borde", v.states.error.borderColor, "states.error.borderColor", setVisual)}
        {color("Error · texto", v.states.error.textColor, "states.error.textColor", setVisual)}
        {color("Error · icono", v.states.error.iconColor, "states.error.iconColor", setVisual)}
      </EditorGroup>
      <SurfaceFields label="Sin resultados · superficie" value={v.states.empty.surface} path="states.empty.surface" setVisual={setVisual} />
      <EditorGroup title="Sin resultados · textos">
        {color("Título", v.states.empty.title.color, "states.empty.title.color", setVisual)}
        {color("Descripción", v.states.empty.description.color, "states.empty.description.color", setVisual)}
      </EditorGroup>
      <ButtonFields label="Sin resultados · CTA" value={v.states.empty.resetButton} path="states.empty.resetButton" setVisual={setVisual} />
    </Stack>
  );
}

export function ProductsHeaderPieceVisualEditor({ owner, visual, setVisual }) {
  const v = resolveProductsPageVisual(visual);
  const labels = { eyebrow: "Texto superior", title: "Título", subtitle: "Subtítulo", results: "Resultados / loading" };
  return (
    <EditorGroup title={`Apariencia · ${labels[owner] || owner}`}>
      {color("Color", v.header[owner].color, `header.${owner}.color`, setVisual)}
    </EditorGroup>
  );
}

export function ProductsSearchVisualEditor({ visual, setVisual }) {
  const v = resolveProductsPageVisual(visual);
  return (
    <Stack spacing={1.5}>
      <SurfaceFields label="Contenedor flotante" value={v.search.shell} path="search.shell" setVisual={setVisual} />
      <EditorGroup title="Campo de búsqueda">
        {color("Fondo", v.search.input.backgroundColor, "search.input.backgroundColor", setVisual)}
        {color("Texto", v.search.input.textColor, "search.input.textColor", setVisual)}
        {color("Placeholder", v.search.input.placeholderColor, "search.input.placeholderColor", setVisual)}
        {color("Icono", v.search.input.iconColor, "search.input.iconColor", setVisual)}
        {color("Borde", v.search.input.borderColor, "search.input.borderColor", setVisual)}
        {color("Borde focus", v.search.input.focusBorderColor, "search.input.focusBorderColor", setVisual)}
        {color("Ring focus", v.search.input.focusRingColor, "search.input.focusRingColor", setVisual)}
        <NumberSetting label="Radio" value={v.search.input.borderRadius} min={0} max={80} onChange={(next) => setVisual("search.input.borderRadius", next)} />
      </EditorGroup>
    </Stack>
  );
}

export function ProductsFiltersVisualEditor({ visual, setVisual }) {
  const v = resolveProductsPageVisual(visual);
  return (
    <Stack spacing={1.5}>
      <SurfaceFields label="Panel desktop" value={v.filters.desktopSurface} path="filters.desktopSurface" setVisual={setVisual} />
      <SurfaceFields label="Drawer mobile" value={v.filters.mobileSurface} path="filters.mobileSurface" setVisual={setVisual} />
      <EditorGroup title="Encabezado y campos">
        {color("Título", v.filters.title.color, "filters.title.color", setVisual)}
        {color("Icono", v.filters.icon.color, "filters.icon.color", setVisual)}
        {color("Campo · fondo", v.filters.field.backgroundColor, "filters.field.backgroundColor", setVisual)}
        {color("Campo · texto", v.filters.field.textColor, "filters.field.textColor", setVisual)}
        {color("Campo · placeholder", v.filters.field.placeholderColor, "filters.field.placeholderColor", setVisual)}
        {color("Campo · label", v.filters.field.labelColor, "filters.field.labelColor", setVisual)}
        {color("Campo · icono", v.filters.field.iconColor, "filters.field.iconColor", setVisual)}
        {color("Campo · borde", v.filters.field.borderColor, "filters.field.borderColor", setVisual)}
        {color("Campo · focus", v.filters.field.focusBorderColor, "filters.field.focusBorderColor", setVisual)}
        {color("Campo · ring focus", v.filters.field.focusRingColor, "filters.field.focusRingColor", setVisual)}
        <NumberSetting label="Campo · radio" value={v.filters.field.borderRadius} min={0} max={80} onChange={(next) => setVisual("filters.field.borderRadius", next)} />
      </EditorGroup>
      <EditorGroup title="Chips">
        {color("Normal · texto", v.filters.chip.textColor, "filters.chip.textColor", setVisual)}
        {color("Normal · fondo", v.filters.chip.backgroundColor, "filters.chip.backgroundColor", setVisual)}
        {color("Normal · borde", v.filters.chip.borderColor, "filters.chip.borderColor", setVisual)}
        {color("Normal · texto hover", v.filters.chip.hoverTextColor, "filters.chip.hoverTextColor", setVisual)}
        {color("Normal · fondo hover", v.filters.chip.hoverBackgroundColor, "filters.chip.hoverBackgroundColor", setVisual)}
        {color("Normal · borde hover", v.filters.chip.hoverBorderColor, "filters.chip.hoverBorderColor", setVisual)}
        {color("Normal · focus", v.filters.chip.focusRingColor, "filters.chip.focusRingColor", setVisual)}
        <Divider />
        {color("Activo · texto", v.filters.activeChip.textColor, "filters.activeChip.textColor", setVisual)}
        {color("Activo · fondo", v.filters.activeChip.backgroundColor, "filters.activeChip.backgroundColor", setVisual)}
        {color("Activo · borde", v.filters.activeChip.borderColor, "filters.activeChip.borderColor", setVisual)}
        {color("Activo · texto hover", v.filters.activeChip.hoverTextColor, "filters.activeChip.hoverTextColor", setVisual)}
        {color("Activo · fondo hover", v.filters.activeChip.hoverBackgroundColor, "filters.activeChip.hoverBackgroundColor", setVisual)}
        {color("Activo · borde hover", v.filters.activeChip.hoverBorderColor, "filters.activeChip.hoverBorderColor", setVisual)}
        {color("Activo · focus", v.filters.activeChip.focusRingColor, "filters.activeChip.focusRingColor", setVisual)}
      </EditorGroup>
      <ButtonFields label="Abrir filtros mobile" value={v.filters.mobileTrigger} path="filters.mobileTrigger" setVisual={setVisual} />
      <ButtonFields label="Limpiar filtros" value={v.filters.resetButton} path="filters.resetButton" setVisual={setVisual} />
      <ButtonFields label="Aplicar filtros" value={v.filters.applyButton} path="filters.applyButton" setVisual={setVisual} />
      <EditorGroup title="Warning de categorías">
        {color("Fondo", v.filters.warning.backgroundColor, "filters.warning.backgroundColor", setVisual)}
        {color("Borde", v.filters.warning.borderColor, "filters.warning.borderColor", setVisual)}
        {color("Texto", v.filters.warning.textColor, "filters.warning.textColor", setVisual)}
        {color("Icono", v.filters.warning.iconColor, "filters.warning.iconColor", setVisual)}
      </EditorGroup>
    </Stack>
  );
}

export function ProductsPaginationVisualEditor({ visual, setVisual }) {
  const v = resolveProductsPageVisual(visual);
  return (
    <Stack spacing={1.5}>
      <ButtonFields label="Anterior" value={v.pagination.previousButton} path="pagination.previousButton" setVisual={setVisual} />
      <EditorGroup title="Contador">
        {color("Texto", v.pagination.counter.color, "pagination.counter.color", setVisual)}
      </EditorGroup>
      <ButtonFields label="Siguiente" value={v.pagination.nextButton} path="pagination.nextButton" setVisual={setVisual} />
      <ButtonFields label="Cargar más" value={v.pagination.loadMoreButton} path="pagination.loadMoreButton" setVisual={setVisual} />
    </Stack>
  );
}

export function ProductsHeaderCtaVisualEditor({ visual, setVisual }) {
  const v = resolveProductsPageVisual(visual);
  return <ButtonFields label="CTA Ver carta" value={v.header.cta} path="header.cta" setVisual={setVisual} />;
}
